import { useEffect, useRef, useState } from "react";
import { isIosDevice, isStandalonePwa } from "../features/notifications/usePushOptIn";
import { isInAppBrowser } from "../core/inAppBrowser";
import { useModalAccessibility } from "../hooks/useModalAccessibility";
import styles from "./PwaInstallBanner.module.css";

const DISMISSED_KEY = "karango_install_dismissed_v1";
const DISMISS_DURATION = 4 * 24 * 60 * 60 * 1000;

function wasDismissed() {
  try {
    const timestamp = Number(window.localStorage.getItem(DISMISSED_KEY));
    return timestamp > 0 && Date.now() - timestamp < DISMISS_DURATION;
  } catch {
    return false;
  }
}

export default function PwaInstallBanner({ isMobile }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalonePwa);
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const dialogRef = useRef(null);
  const ios = isIosDevice();
  const visible = isMobile && !installed && !dismissed && !isInAppBrowser() && (ios || Boolean(installPrompt) || error);
  const helpOpen = Boolean(visible && showHelp);
  useModalAccessibility(dialogRef, { active: helpOpen });

  useEffect(() => {
    const media = window.matchMedia("(display-mode: standalone)");
    const syncInstalled = () => setInstalled(isStandalonePwa());
    const handlePrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setError(false);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setShowHelp(false);
    };
    window.addEventListener("beforeinstallprompt", handlePrompt);
    window.addEventListener("appinstalled", handleInstalled);
    media.addEventListener?.("change", syncInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      media.removeEventListener?.("change", syncInstalled);
    };
  }, []);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowHelp(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [helpOpen]);

  const dismiss = () => {
    setDismissed(true);
    setShowHelp(false);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // La fermeture reste effective pour cette session.
    }
  };

  const install = async () => {
    if (ios) {
      setShowHelp(true);
      return;
    }
    if (!installPrompt || busy) return;
    setBusy(true);
    setError(false);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      // Un evenement d'installation ne peut etre utilise qu'une fois.
      setInstallPrompt(null);
      if (choice.outcome === "accepted") setDismissed(true);
      else dismiss();
    } catch {
      setInstallPrompt(null);
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <section className={styles.banner} aria-label="Installer Karango">
        <img className={styles.icon} src="/icon-192.png" alt="" />
        <div className={styles.copy}>
          <strong>Installer Karango</strong>
          <p>Accède plus vite à tes sorties depuis ton écran d’accueil.</p>
        </div>
        <button className={styles.close} type="button" onClick={dismiss} aria-label="Fermer la bannière d’installation">×</button>
        <button className={styles.install} type="button" onClick={install} disabled={busy || (!ios && !installPrompt)}>
          {busy ? "Installation…" : "Installer"}
        </button>
        {error ? <p role="status">L’installation n’a pas pu démarrer. Réessaie après avoir rechargé Karango.</p> : null}
      </section>
      {helpOpen ? (
        <div className={styles.overlay} onClick={() => setShowHelp(false)}>
          <section ref={dialogRef} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="install-ios-title" tabIndex={-1} onClick={(event) => event.stopPropagation()}>
            <h2 id="install-ios-title">Installer Karango sur iPhone</h2>
            <ol>
              <li>Ouvre Karango dans <strong>Safari</strong>.</li>
              <li>Touche <strong>Partager</strong> (le carré avec une flèche vers le haut). Si tu vois plutôt un bouton <strong>…</strong>, ouvre ce menu puis touche <strong>Partager</strong>.</li>
              <li>Fais défiler les actions et choisis <strong>Sur l’écran d’accueil</strong>.</li>
              <li>Si l’option <strong>Ouvrir comme app web</strong> apparaît, active-la, puis touche <strong>Ajouter</strong>.</li>
              <li>Ouvre Karango depuis sa nouvelle icône.</li>
            </ol>
            <p>Tu ne vois pas « Sur l’écran d’accueil » ? En bas de la liste de partage, touche <strong>Modifier les actions</strong> et ajoute cette option.</p>
            <p>Tu pourras ensuite activer les notifications dans Karango.</p>
            <button className={styles.install} type="button" onClick={() => setShowHelp(false)}>J’ai compris</button>
          </section>
        </div>
      ) : null}
    </>
  );
}
